import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Package, Search, Check, ChevronDown, ChevronUp, X, Plus, Carrot, Beef, Milk, Wheat, Apple } from 'lucide-react';
import { Button } from './ui/button';
import { pantryAPI } from '../utils/api';

// Unified categories matching backend Schema A (same as Pantry.jsx and Grocery.jsx)
var CATEGORIES = [
  { id: 'produce', name: 'Produce', icon: Carrot, color: 'text-green-500' },
  { id: 'proteins', name: 'Proteins', icon: Beef, color: 'text-red-500' },
  { id: 'dairy', name: 'Dairy & Eggs', icon: Milk, color: 'text-blue-400' },
  { id: 'pantry', name: 'Pantry', icon: Wheat, color: 'text-amber-500' },
  { id: 'other', name: 'Other', icon: Apple, color: 'text-purple-500' }
];

var CATEGORY_IDS = CATEGORIES.map(function(c) { return c.id; });

function nameKey(name) {
  return String(name || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function PantrySelector({ selectedItems, onItemsChange, onClose }) {
  var [searchTerm, setSearchTerm] = useState('');
  var [commonItems, setCommonItems] = useState(null);
  var [expandedCategory, setExpandedCategory] = useState('proteins');
  var [customItem, setCustomItem] = useState('');
  var [customCategory, setCustomCategory] = useState('other');
  var [loading, setLoading] = useState(true);
  // Identity behind each listed name: a Core Food preset {food_id, category},
  // a saved Pantry item, or a custom entry {category}. Selection itself stays
  // a list of names (the parent screens' contract).
  var identityRef = useRef({});
  // The user's saved Pantry (null when it couldn't be loaded)
  var savedPantryRef = useRef(null);

  // Fetch the Core-Food-backed presets AND the user's saved pantry on mount
  useEffect(function() {
    function mergePresets(commonData) {
      var merged = {};
      CATEGORY_IDS.forEach(function(cat) {
        merged[cat] = [];
        ((commonData && commonData[cat]) || []).forEach(function(item) {
          // Only Core-Food-backed presets are selectable; anything else is ignored.
          if (!item || typeof item !== 'object' || !item.food_id || !item.name) return;
          var key = nameKey(item.name);
          if (identityRef.current[key]) return;
          identityRef.current[key] = { kind: 'preset', food_id: item.food_id, category: cat };
          merged[cat].push(item.name);
        });
      });
      return merged;
    }

    Promise.all([
      pantryAPI.getCommonItems(),
      pantryAPI.getPantry()
    ])
      .then(function(results) {
        var mergedItems = mergePresets(results[0].data);
        var savedPantry = results[1].data || {};
        savedPantryRef.current = savedPantry;

        // Add saved pantry items not already listed
        CATEGORY_IDS.forEach(function(cat) {
          var items = savedPantry[cat] || [];
          items.forEach(function(item) {
            if (item && item.name) {
              var key = nameKey(item.name);
              var listed = identityRef.current[key];
              identityRef.current[key] = { kind: 'saved', food_id: item.food_id || (listed && listed.food_id) || null,
                category: cat };
              var exists = mergedItems[cat].some(function(existing) { return nameKey(existing) === key; });
              if (!exists) {
                mergedItems[cat].push(item.name);
              }
            }
          });
        });

        setCommonItems(mergedItems);
        setLoading(false);
      })
      .catch(function(err) {
        console.error('Failed to load pantry items:', err);
        pantryAPI.getCommonItems()
          .then(function(res) {
            setCommonItems(mergePresets(res.data));
          })
          .finally(function() {
            setLoading(false);
          });
      });
  }, []);

  function toggleItem(item) {
    var itemLower = item.toLowerCase();
    var newItems = selectedItems.slice();
    var idx = newItems.findIndex(function(i) { return i.toLowerCase() === itemLower; });

    if (idx >= 0) {
      newItems.splice(idx, 1);
    } else {
      newItems.push(item);
    }

    onItemsChange(newItems);
  }

  function isSelected(item) {
    var itemLower = item.toLowerCase();
    return selectedItems.some(function(i) { return i.toLowerCase() === itemLower; });
  }

  function addCustomItem() {
    if (!customItem.trim()) return;
    var key = nameKey(customItem);
    if (!identityRef.current[key]) {
      identityRef.current[key] = { kind: 'custom', category: customCategory || 'other' };
    }
    if (!isSelected(customItem)) {
      onItemsChange(selectedItems.concat([customItem.trim()]));

      // Add to commonItems display so it shows in the correct category
      if (commonItems) {
        var cat = customCategory || 'other';
        var updated = Object.assign({}, commonItems);
        if (!updated[cat]) updated[cat] = [];
        var alreadyListed = updated[cat].some(function(existing) {
          return existing.toLowerCase() === customItem.trim().toLowerCase();
        });
        if (!alreadyListed) {
          updated[cat] = updated[cat].concat([customItem.trim()]);
          setCommonItems(updated);
        }
      }
    }
    setCustomItem('');
  }

  function filterItems(items) {
    if (!searchTerm) return items;
    var term = searchTerm.toLowerCase();
    return items.filter(function(item) {
      return item.toLowerCase().includes(term);
    });
  }

  // Save, then close. Genuinely new picks go through POST /pantry/items (a
  // preset keeps its Core Food; a custom entry is resolved by the server and
  // keeps the chosen section only while unresolved). Saved items the user
  // deselected are then removed with one whole-Pantry PUT built from the
  // server's own items, so their identities are preserved.
  function handleDone() {
    var saved = savedPantryRef.current;
    var savedKeys = {};
    CATEGORY_IDS.forEach(function(cat) {
      ((saved && saved[cat]) || []).forEach(function(item) {
        if (item && item.name) savedKeys[nameKey(item.name)] = true;
      });
    });
    var selectedKeys = {};
    selectedItems.forEach(function(item) { selectedKeys[nameKey(item)] = true; });

    var additions = [];
    selectedItems.forEach(function(item) {
      var key = nameKey(item);
      if (savedKeys[key]) return;
      var identity = identityRef.current[key] || {};
      if (identity.kind === 'preset') {
        additions.push({ food_id: identity.food_id, name: item });
      } else if (identity.kind === 'custom') {
        additions.push({ name: item, category: identity.category });
      } else {
        additions.push({ name: item });
      }
    });
    var hasDeselected = Object.keys(savedKeys).some(function(key) { return !selectedKeys[key]; });

    var added = additions.length
      ? pantryAPI.addItems(additions, 'pantry_selector').then(function(res) { return (res.data || {}).pantry; })
      : Promise.resolve(saved);
    added
      .then(function(current) {
        if (!saved || !hasDeselected || !current) return null;
        var kept = {};
        CATEGORY_IDS.forEach(function(cat) {
          kept[cat] = (current[cat] || []).filter(function(item) {
            var name = item && (item.name || item);
            return selectedKeys[nameKey(name)];
          });
        });
        return pantryAPI.updatePantry(kept);
      })
      .catch(function(err) {
        console.error('Failed to save pantry on done:', err);
      });
    onClose();
  }

  // Build category sections
  var categorySections = [];
  if (commonItems) {
    CATEGORIES.forEach(function(category) {
      var id = category.id;
      var items = commonItems[id] || [];
      var filteredItems = filterItems(items);
      var isExpanded = expandedCategory === id || searchTerm.length > 0;

      if (filteredItems.length === 0 && searchTerm) return;

      var selectedInCategory = filteredItems.filter(function(item) { return isSelected(item); }).length;

      var Icon = category.icon;

      var itemButtons = filteredItems.map(function(item) {
        var selected = isSelected(item);
        return (
          <button
            key={item}
            onClick={function() { toggleItem(item); }}
            className={'px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ' +
              (selected
                ? 'bg-accent text-white'
                : 'bg-secondary/50 hover:bg-secondary text-foreground')}
            data-testid={'pantry-item-' + item.replace(/\s+/g, '-').toLowerCase()}
          >
            {selected && <Check size={14} />}
            {item}
          </button>
        );
      });

      var handleCategoryClick = (function(cat) {
        return function() {
          setExpandedCategory(function(current) {
            return current === cat ? null : cat;
          });
        };
      })(id);

      categorySections.push(
        <div key={id} className="border-b border-border/30 last:border-0">
          <button
            onClick={handleCategoryClick}
            className="w-full flex items-center justify-between p-3 hover:bg-secondary/30 transition-colors"
            data-testid={'category-' + id}
          >
            <div className="flex items-center gap-2">
              <Icon size={18} className={category.color} />
              <span className="font-medium">{category.name}</span>
              {selectedInCategory > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-accent text-white text-xs">
                  {selectedInCategory}
                </span>
              )}
            </div>
            {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
          </button>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="flex flex-wrap gap-2 p-3 pt-0">
                  {itemButtons}
                  {filteredItems.length === 0 && !searchTerm && (
                    <p className="text-xs text-muted-foreground py-1">No items yet</p>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    });
  }

  // Selected items chips
  var selectedChips = selectedItems.map(function(item) {
    return (
      <div
        key={item}
        className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent/10 text-accent text-xs"
      >
        <span>{item}</span>
        <button
          onClick={function() { toggleItem(item); }}
          className="hover:bg-accent/20 rounded-full p-0.5"
          data-testid={'remove-pantry-' + item.replace(/\s+/g, '-').toLowerCase()}
        >
          <X size={12} />
        </button>
      </div>
    );
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 0 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      className="bg-card border border-border/40 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-border/40 bg-secondary/20">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package size={20} className="text-accent" />
            <h3 className="font-heading font-semibold">My Pantry</h3>
          </div>
          <span className="text-xs text-muted-foreground">
            {selectedItems.length} items selected
          </span>
        </div>

        <p className="text-sm text-muted-foreground mb-3">
          Select ingredients you have. Smart Plan will prioritize meals using these items.
        </p>

        {/* Search */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
            placeholder="Search ingredients..."
            className="w-full h-10 pl-10 pr-4 rounded-lg border border-border/40 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            data-testid="pantry-search"
          />
        </div>
      </div>

      {/* Selected Items */}
      {selectedItems.length > 0 && (
        <div className="p-3 border-b border-border/40 bg-accent/5">
          <p className="text-xs text-muted-foreground mb-2">Selected:</p>
          <div className="flex flex-wrap gap-1">
            {selectedChips}
          </div>
        </div>
      )}

      {/* Categories */}
      <div className="max-h-[50vh] overflow-y-auto">
        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
            <p className="text-sm text-muted-foreground">Loading ingredients...</p>
          </div>
        ) : (
          categorySections
        )}
      </div>

      {/* Add Custom Item with Category Picker */}
      <div className="p-3 border-t border-border/40 bg-secondary/10">
        <p className="text-xs text-muted-foreground mb-2">Add custom ingredient:</p>
        <div className="flex gap-2">
          <select
            value={customCategory}
            onChange={function(e) { setCustomCategory(e.target.value); }}
            className="h-9 px-2 rounded-lg border border-border/40 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            data-testid="custom-category-select"
          >
            {CATEGORIES.map(function(cat) {
              return <option key={cat.id} value={cat.id}>{cat.name}</option>;
            })}
          </select>
          <input
            type="text"
            value={customItem}
            onChange={function(e) { setCustomItem(e.target.value); }}
            onKeyPress={function(e) { if (e.key === 'Enter') addCustomItem(); }}
            placeholder="e.g., Tempeh"
            className="flex-1 h-9 px-3 rounded-lg border border-border/40 bg-background text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
            data-testid="custom-ingredient-input"
          />
          <Button
            onClick={addCustomItem}
            disabled={!customItem.trim()}
            size="sm"
            className="h-9 px-3 rounded-lg bg-accent text-white"
            data-testid="add-custom-ingredient-btn"
          >
            <Plus size={16} />
          </Button>
        </div>
      </div>

      {/* Done Button */}
      <div className="p-3 border-t border-border/40">
        <Button
          onClick={handleDone}
          className="w-full h-10 rounded-lg bg-accent text-white font-medium"
          data-testid="pantry-done-btn"
        >
          Done ({selectedItems.length} items)
        </Button>
      </div>
    </motion.div>
  );
}

export default PantrySelector;
